import { useState, useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { HamburgerIcon } from "../atoms/HamburgerIcon";
import { usePublicCategories } from "../../hooks/useFirestore";
import styled from 'styled-components';

const NavContainer = styled.nav`
  display: flex;
  gap: 2rem;
  align-items: center;
  position: relative;

  @media (max-width: 768px) {
    justify-content: flex-end;
  }
`;

// Desktop Navigation
const DesktopNavWrapper = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

// Mobile Navigation
const MobileNavWrapper = styled.div<{ $isOpen: boolean }>`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 100%;
    right: 0;
    background: ${props => props.theme.colors.cardBackground};
    border: 1px solid ${props => props.theme.colors.border};
    border-radius: 8px;
    box-shadow: 0 4px 20px ${props => props.theme.colors.shadow};
    min-width: 200px;
    z-index: 1000;
    opacity: ${props => props.$isOpen ? 1 : 0};
    visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
    transform: translateY(${props => props.$isOpen ? '0' : '-10px'});
    transition: all 0.3s ease;
    padding: 0.5rem 0;
  }
`;

const NavItem = styled(NavLink)`
  color: ${props => props.theme.colors.text};
  text-decoration: none;
  font-weight: 500;
  font-size: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  border-bottom: 2px solid transparent;

  &:hover {
    color: ${props => props.theme.colors.primary};
    border-bottom: 2px solid ${props => props.theme.colors.primary};
  }

  &.active {
    color: ${props => props.theme.colors.primary};
    border-bottom: 2px solid ${props => props.theme.colors.primary};
  }

  @media (max-width: 768px) {
    border-bottom: none;
    border-radius: 0;
    padding: 0.75rem 1rem;
    width: 100%;
    display: block;
    
    &:hover {
      background: ${props => props.theme.colors.primary}1a;
      border-bottom: none;
    }

    &.active {
      background: ${props => props.theme.colors.primary};
      color: white;
      border-bottom: none;
    }
  }
`;

const DropdownContainer = styled.div`
  position: relative;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const DropdownMenu = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  min-width: 200px;
  z-index: 1000;
  opacity: ${props => props.$isVisible ? 1 : 0};
  visibility: ${props => props.$isVisible ? 'visible' : 'hidden'};
  transform: translateY(${props => props.$isVisible ? '0' : '-10px'});
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    position: static;
    box-shadow: none;
    border: none;
    border-radius: 0;
    opacity: 1;
    visibility: visible;
    transform: none;
    background: #f8f8f8;
    margin-left: 1rem;
  }
`;

const DropdownItem = styled(NavLink)`
  display: block;
  color: #333;
  text-decoration: none;
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(255, 2, 2, 0.1);
    color: rgba(255, 2, 2, 1);
  }

  &.active {
    background: rgba(255, 2, 2, 1);
    color: white;
  }

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
  }
`;

export const Navigation = () => {
  const { categories } = usePublicCategories();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Dropdown dışına tıklanınca kapat
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleMobileMenu = () => setIsMobileMenuOpen(prev => !prev);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <NavContainer ref={mobileMenuRef}>
      {/* Desktop Navigation */}
      <DesktopNavWrapper>
        <NavItem to="/" end>
          Anasayfa
        </NavItem>
        
        <DropdownContainer
          ref={dropdownRef}
          onMouseEnter={() => setIsDropdownOpen(true)}
          onMouseLeave={() => setIsDropdownOpen(false)}
        >
          <NavItem to="/products">
            Kategoriler
          </NavItem>
          
          <DropdownMenu $isVisible={isDropdownOpen}>
            {categories.map((category) => (
              <DropdownItem 
                key={category.id} 
                to={`/products/${category.slug}`}
                onClick={() => setIsDropdownOpen(false)}
              >
                {category.name}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </DropdownContainer>
        
        <NavItem to="/gallery">
          Galeri
        </NavItem>
        
        <NavItem to="/contact">
          İletişim
        </NavItem>
      </DesktopNavWrapper>

      {/* Mobile Hamburger Icon */}
      <HamburgerIcon isOpen={isMobileMenuOpen} onClick={toggleMobileMenu} />

      {/* Mobile Navigation */}
      <MobileNavWrapper $isOpen={isMobileMenuOpen}>
        <NavItem to="/" onClick={closeMobileMenu}>
          Anasayfa
        </NavItem>
        
        <DropdownContainer>
          <NavItem to="/products" onClick={closeMobileMenu}>
            Kategoriler
          </NavItem>
          
          
        </DropdownContainer>
        
        <NavItem to="/gallery" onClick={closeMobileMenu}>
          Galeri
        </NavItem>
        
        <NavItem to="/contact" onClick={closeMobileMenu}>
          İletişim
        </NavItem>
      </MobileNavWrapper>
    </NavContainer>
  );
};